/**
 * Landing — the public events list (workspaces/{orgId}/events,
 * status == 'published'). Pure state; landing.sync.ts (web-side, a boot
 * reconciler mirroring workspace.sync.ts) is the sole writer.
 */

import { formatDateRange } from "@mhmo91/rx";
import { computed, state } from "@mhmo91/schmancy/state";
import { resolveLocalizedText } from "@momo/i18n";
import { Event, Location } from "@momo/types";
import { uiLanguage } from "../../i18n/localize.ts";
import { type LocationWithId, locationsState } from "../locations.state.ts";
import { refundWindowPhase } from "../refund/window.state.ts";

export type PublishedEvent = Event.Event & {
	id: string;
	/**
	 * Resolved display URL for `poster` (a Storage object path per
	 * Event.Event's own field) — no entity counterpart, since resolving it
	 * (getDownloadURL) is genuine I/O this pure state file can't perform.
	 * landing.api.ts (web-side) populates it at the read seam. null when no
	 * poster is set or the resolve failed.
	 */
	posterUrl: string | null;
};

export type LandingState = {
	readonly status: "loading" | "ready";
	readonly events: readonly PublishedEvent[];
};

const initial: LandingState = { status: "loading", events: [] };

// keepOnClear: the published-events list is public and identity-free — a
// sign-in or sign-out elsewhere in the same tab must not wipe it. Its sole
// writer is a live Firestore snapshot listener (landing.sync.ts) that only
// re-emits when the list itself changes, so a clearStates() reset would
// strand it at `initial` with nothing to repopulate it.
export const landingState = state<LandingState>("storefront/landing").memory(
	initial,
	{ keepOnClear: true },
);

/**
 * The one reading of "over" this page splits on — the operator console's own
 * lifecycle, not a second one minted here, so the shelf and the console agree
 * about the same event. The boundary is `end` strictly: an event that has
 * started and is still running reads `present`, keeps its place on the shelf,
 * and only leaves once its last minute has passed.
 *
 * The clock is no signal — the split re-evaluates when some other dependency
 * of the reading computed emits, exactly as refundWindowPhase and the console
 * do. An event crossing `end` under a page left open does not migrate until
 * the listener re-emits.
 */
function hasEnded(event: Pick<PublishedEvent, "schedule">, now: Date): boolean {
	return (
		Event.resolveLifecycle(now, {
			start: event.schedule.start.toDate(),
			end: event.schedule.end.toDate(),
		}) === "after"
	);
}

/** Which shape the settled list renders: one event whose poster is a shelf's
 *  one tile, one event with no poster (a full-width card sized by its own
 *  type), or a shelf of two or more tiles. Only the `single-text` loading frame
 *  reads `hasPast`, because the posterless card's vertical placement turns on it
 *  (landing.view.ts). The other members still carry it because this value is
 *  persisted in buyers' storage, which is also why `single-poster` stays its own
 *  kind rather than a shelf of one; its tile goes square. */
export type SettledFrame =
	| { readonly kind: "single-poster"; readonly hasPast: boolean }
	| { readonly kind: "single-text"; readonly hasPast: boolean }
	| {
			readonly kind: "shelf";
			readonly count: number;
			readonly hasPast: boolean;
	  };

/**
 * The shape the last settled visit rendered, so the loading frame renders that
 * same shape rather than a cardinality and a layout of its own invention.
 * `single-poster` on a first-ever visit: nothing is known about the list yet,
 * and one poster tile is the smallest claim that is also the shape a one-event
 * deployment settles into.
 *
 * keepOnClear, for landingState's reason: the published list is public and
 * identity-free, so a sign-in or sign-out elsewhere in the tab must not reset
 * what the buyer's last visit already established.
 */
export const lastSettledFrame = state<SettledFrame>(
	"storefront/landing/settled-frame",
).local({ kind: "single-poster", hasPast: false }, { keepOnClear: true });

/** Every consumer's reconciler records the settled shape through here, so the
 *  loading frame's guess is derived one way for all of them. An empty list has
 *  no card to mirror, so it records the shape with the least ink of the three
 *  rather than a shelf of nothing.
 *
 *  The split is here rather than in each caller: both reconcilers
 *  (web's and the studio's) hand this the whole list, and one rule for the two
 *  of them is what keeps them from drifting. `count` counts the upcoming only
 *  — a skeleton tile per archived event would place placeholders for cards
 *  that never arrive, which is the one thing this mechanism exists to
 *  prevent. */
export function recordSettledFrame(events: readonly PublishedEvent[]) {
	const now = new Date();
	const upcoming = events.filter((event) => !hasEnded(event, now));
	const hasPast = upcoming.length < events.length;
	// replace, not set: a partial merge would leave the shelf's count standing
	// on a shape that has no count.
	const [first] = upcoming;
	if (upcoming.length > 1)
		return lastSettledFrame.replace({
			kind: "shelf",
			count: upcoming.length,
			hasPast,
		});
	if (first === undefined || first.posterUrl === null)
		return lastSettledFrame.replace({ kind: "single-text", hasPast });
	return lastSettledFrame.replace({ kind: "single-poster", hasPast });
}

export type LandingRow = {
	readonly id: string;
	readonly name: string;
	readonly poster: string | null;
	readonly dateLabel: string;
	readonly venue: string | null;
	/** The event's own words in the reader's language, resolved here rather
	 *  than at the card, because the archived card opens them in a reading
	 *  overlay and every other fact that reading shows already comes off this
	 *  row. null when the operator wrote none. */
	readonly description: string | null;
	/** every entry in the event's availability rollup has latched soldOut —
	 *  absent availability (undecided yet) never reads as sold out. */
	readonly soldOut: boolean;
	/** this event is taking tickets back right now — an absent `refund` means
	 *  returns are closed and always were, so the card offers no way in. */
	readonly refundOpen: boolean;
};

/** The card's own view-model — venue resolved against the shared location
 *  catalog, sold-out derived from the event's availability rollup, a
 *  dateLabel in the UI language, poster the event's own resolved posterUrl. One projection for
 *  both shelves: an archived card carries the identical facts, and whether it
 *  is over is its membership in `pastRows`, never a field beside these that
 *  would be free to disagree with that membership. */
function toRow(
	event: PublishedEvent,
	catalog: readonly LocationWithId[],
): LandingRow {
	const location = catalog.find((l) => l.id === event.locationId);
	const parent = location?.within
		? catalog.find((l) => l.id === location.within)
		: undefined;
	const venue = location
		? parent
			? `${parent.name} — ${location.name}`
			: location.name
		: null;

	const buckets = event.availability ? Object.values(event.availability) : [];

	return {
		id: event.id,
		name: event.name,
		poster: event.posterUrl,
		// Venue wall-clock date range, never the visitor's own browser zone.
		dateLabel: formatDateRange(
			event.schedule.start.toDate(),
			event.schedule.end.toDate(),
			{
				locale: uiLanguage() === "de" ? "de-DE" : "en-GB",
				month: "long",
				timeZone:
					location?.timezone ??
					Location.LocationSchema.shape.timezone.parse(undefined),
			},
		),
		venue,
		description: resolveLocalizedText(
			event.descriptionByLocale,
			event.description,
			uiLanguage(),
		),
		soldOut: buckets.length > 0 && buckets.every((b) => b.state === "soldOut"),
		// The one reading of the window every refund surface shares — the
		// card must not offer a door the return page then shuts.
		refundOpen: refundWindowPhase(event) === "open",
	};
}

/** The shelf: everything still ahead or still running, soonest first. */
export const upcomingRows = computed<readonly LandingRow[]>(() => {
	const catalog = locationsState.value;
	const now = new Date();
	return landingState.value.events
		.filter((event) => !hasEnded(event, now))
		.toSorted(
			(a, b) => a.schedule.start.toMillis() - b.schedule.start.toMillis(),
		)
		.map((event) => toRow(event, catalog));
});

/** The archive: whatever the shelf did not take, most recently finished
 *  first — so in both sections the row nearest to now sits closest to that
 *  section's own top.
 *
 *  The complement, never a second reading of the clock: one reading decides
 *  which side an event falls on, so the two sections are a total and disjoint
 *  partition of the same list by construction and no event can land in both
 *  or in neither whatever the clock does between the two computeds. */
export const pastRows = computed<readonly LandingRow[]>(() => {
	const catalog = locationsState.value;
	const onTheShelf = new Set(upcomingRows.get().map((row) => row.id));
	return landingState.value.events
		.filter((event) => !onTheShelf.has(event.id))
		.toSorted((a, b) => b.schedule.end.toMillis() - a.schedule.end.toMillis())
		.map((event) => toRow(event, catalog));
});

/** Which frame the view renders — exactly one. `past-only` is the other side
 *  of `empty`: none right now, versus never any. */
export const landingPhase = computed<
	"loading" | "empty" | "past-only" | "populated"
>(() => {
	if (landingState.value.status === "loading") return "loading";
	if (upcomingRows.get().length > 0) return "populated";
	return pastRows.get().length === 0 ? "empty" : "past-only";
});

/** Whether the page pins its footer to the viewport floor: only a shelf of two
 *  or more upcoming events does, or the loading frame standing in for one. An
 *  empty or past-only list records a `single-text` frame, so the loading arm
 *  names the shelf rather than excluding the single kinds. */
export const footerPinned = computed<boolean>(() =>
	landingPhase.get() === "loading"
		? lastSettledFrame.value.kind === "shelf"
		: upcomingRows.get().length > 1,
);

/** The buyer's own tap on the archive, null until they make one — a UI-only
 *  fact with no entity counterpart. `.memory()`, never `.local()`: a drawer
 *  shut last month must not stay shut over a refund window opened since. */
export const archiveOpenChoice = state<boolean | null>(
	"storefront/landing/archive-open",
).memory(null);

/** A closed disclosure claims that nothing below it needs the buyer. True of
 *  ordinary past events, false when one of them is handing money back, and
 *  false when the archive is the only content the page has — so the default
 *  is derived from the page's own facts and the buyer's tap overrides it from
 *  then on. */
export const archiveOpen = computed<boolean>(() => {
	const chosen = archiveOpenChoice.value;
	if (chosen !== null) return chosen;
	return (
		upcomingRows.get().length === 0 ||
		pastRows.get().some((row) => row.refundOpen)
	);
});
